import "./admin-organization.scss";

import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../features/helpers"
import { getOrganizations, getOrganizationsList, addOrganizationWithOwner, changeOrganization, getOrganizationModalStatus, setModalOrganization, NewOwnerInput } from "../../../store/OrganizationSlice";
import { Eye, EyeClosed, X } from "lucide-react";

import CustomList from "../../../components/custom-list/custom-list";

const EMPTY_OWNER: NewOwnerInput & { password: string } = {
    name: '',
    email: '',
    login: '',
    password: '',
};

const Organization = () => {

    const dispatch = useAppDispatch();

    const list: Array<any> | null = useAppSelector(getOrganizationsList);
    const modalStatus: boolean = useAppSelector(getOrganizationModalStatus);

    const[organizationName, setOrganizationName] = useState<string>('');

    const [showOwnerPassword, setShowOwnerPassword] = useState(false);
    const[edit, setEdit] = useState<boolean>(false);
    const[status, setStatus] = useState<string>('');
    const[id, setId] = useState<number>(0);

    // Fluxo de criacao em 2 passos: 1) empresa, 2) owner da empresa.
    const[step, setStep] = useState<1 | 2>(1);
    const[owner, setOwner] = useState(EMPTY_OWNER);
    const[formError, setFormError] = useState<string>('');
    const[saving, setSaving] = useState<boolean>(false);

    useEffect( () => {
        dispatch(getOrganizations());
    }, [dispatch]);

    const resetForm = () => {
        setEdit(false);
        setOrganizationName('');
        setStatus('');
        setStep(1);
        setOwner(EMPTY_OWNER);
        setFormError('');
    }

    const toggle = () => {
        if (modalStatus) resetForm(); // fechando -> limpa o form
        dispatch(setModalOrganization(!modalStatus))
    }

    const editItem = (id:number) => {
        const filtered = list?.find( item => item.id === id)
        setOrganizationName(filtered.nome)
        setId(id);
        setEdit(true);
        toggle();
    }

    const handleOwnerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setOwner(prev => ({ ...prev, [name]: value }));
    }

    // Passo 1 -> 2: valida o nome da empresa antes de avancar.
    const goToOwnerStep = () => {
        if (!organizationName.trim()) {
            setFormError('Informe o nome da empresa.');
            return;
        }
        setFormError('');
        setStep(2);
    }

    const handleEdit = async () => {
        if (!status) {
            setFormError('Selecione o status.');
            return;
        }
        setFormError('');
        try {
            await dispatch(changeOrganization(JSON.parse(status), id));
            resetForm(); // modal ja fechado pela getOrganizations; limpa o estado de edicao
        } catch (e: any) {
            setFormError(e?.message ?? 'Falha ao editar a organizacao.');
        }
    }

    // Passo 2: cria empresa + owner num so fluxo.
    const handleCreate = async () => {
        if (!owner.name.trim() || !owner.email.trim() || !owner.login.trim()) {
            setFormError('Preencha nome, e-mail e login do owner.');
            return;
        }
        setFormError('');
        setSaving(true);
        try {
            const ok = await dispatch(addOrganizationWithOwner(organizationName, owner));
            if (ok) resetForm(); // o modal e fechado pela getOrganizations
            else setFormError('Nao foi possivel criar a empresa e o owner.');
        } catch (e: any) {
            setFormError(e?.message ?? 'Falha ao criar empresa e owner.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <div className="organization-page">
            <div className="actions-title">
                <h2 className="page-title">
                    Organizacoes
                    <small>
                        lista de oraganizacoes cadastyradas na plataforma
                    </small>
                </h2>
                <div className="buttons">
                    <div className="btn ghost" onClick={toggle}>Adicionar</div>
                </div>
            </div>

            <div className="organization-wrapper">
                { list && (
                    <div className="organizations-custom-list">
                        <CustomList title="Organizoes" subTitle="lista de organizacoes ativas na plataforma" data={list} options={true} editItem={editItem}/>
                    </div>
                )}
            </div>

            <div className={`modal ${!modalStatus ? 'hide' : ''}`}>
                <div className="shadow" onClick={toggle}></div>
                <div className="modal-body">
                    <h2>
                        <span>
                            { edit
                                ? 'Editar Organizacao'
                                : step === 1 ? 'Nova empresa — Passo 1 de 2' : 'Nova empresa — Passo 2 de 2' }
                        </span>
                        <X size={40} onClick={toggle}/>
                    </h2>

                    { edit ? (
                        /* ----- EDICAO: nome (read-only) + status ----- */
                        <>
                            <div className="form-group">
                                <label>Nome:</label>
                                <input type="text" disabled value={organizationName} />
                            </div>
                            <div className="form-group">
                                <label>Status:</label>
                                <select value={status} onChange={e => setStatus(e.target.value)}>
                                    <option value="">Selecione o Status</option>
                                    <option value="true">Ativa</option>
                                    <option value="false">Desativada</option>
                                </select>
                            </div>
                            { formError && <small className="form-error">{formError}</small> }
                            <div className="btn purple" onClick={handleEdit}>Editar</div>
                        </>
                    ) : step === 1 ? (
                        /* ----- CRIACAO PASSO 1: nome da empresa ----- */
                        <>
                            <div className="form-group">
                                <label>Nome da empresa:</label>
                                <input
                                    type="text"
                                    value={organizationName}
                                    onChange={e => setOrganizationName(e.target.value)}
                                    placeholder="Nome da empresa"
                                />
                            </div>
                            { formError && <small className="form-error">{formError}</small> }
                            <div className="btn purple" onClick={goToOwnerStep}>Proximo</div>
                        </>
                    ) : (
                        /* ----- CRIACAO PASSO 2: owner da empresa ----- */
                        <>
                            <p className="owner-step-hint">Crie o usuario owner que vai administrar <strong>{organizationName}</strong>.</p>
                            <div className="form-group">
                                <label>Nome do owner:</label>
                                <input type="text" name="name" value={owner.name} onChange={handleOwnerChange} placeholder="Nome do responsavel" />
                            </div>
                            <div className="form-group">
                                <label>E-mail:</label>
                                <input type="text" name="email" value={owner.email} onChange={handleOwnerChange} placeholder="email@empresa.com" />
                            </div>
                            <div className="form-group">
                                <label>Login (usado para login):</label>
                                <input type="text" name="login" value={owner.login} onChange={handleOwnerChange} placeholder="login de acesso" />
                            </div>
                            <div className="form-group password-field">
                                <label>Senha (opcional — gerada se vazia):</label>
                                <input type={showOwnerPassword ? "text" : "password"} name="password" value={owner.password} onChange={handleOwnerChange} placeholder="senha inicial" />
                                <button type="button" className="toggle"
                                    aria-label={showOwnerPassword ? 'Ocultar senha' : 'Mostrar senha'}
                                    onClick={() => setShowOwnerPassword(v => !v)}>
                                    { showOwnerPassword ? <Eye size={18} /> : <EyeClosed size={18} /> }
                                </button>
                            </div>
                            { formError && <small className="form-error">{formError}</small> }
                            <div className="buttons">
                                <div className="btn ghost" onClick={() => { setFormError(''); setStep(1); }}>Voltar</div>
                                <div className={`btn purple ${saving ? 'disabled' : ''}`} onClick={saving ? undefined : handleCreate}>
                                    { saving ? 'Criando...' : 'Criar empresa' }
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

export default Organization;
